import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { animate, createScope, spring, stagger } from 'animejs';

// Keep motion separate from navigation, form submission, and calculation state.
export default function SiteMotion({ reducedMotion }) {
  const { pathname } = useLocation();

  useEffect(() => {
    if (reducedMotion || pathname === '/personal' || !('IntersectionObserver' in window)) return undefined;
    const listeners = [];
    const scope = createScope({ root: document.body }).add((self) => {
      const entrance = document.querySelectorAll('.zema-home-hero-copy > *, .auth-card');
      if (entrance.length) animate(entrance, {
        opacity: [0, 1], y: [18, 0], duration: 650,
        delay: stagger(65), ease: 'out(3)',
      });
      self.add('reveal', (element) => animate(element, {
        opacity: [.35, 1], y: [24, 0], duration: 650, ease: 'out(3)',
      }));
      self.add('chart', (element) => animate(element.querySelectorAll('i'), {
        scaleY: [.2, 1], delay: stagger(55), duration: 550, ease: 'out(3)',
      }));
      self.add('pipeline', (element) => animate(element.querySelectorAll('.zema-pipeline-stages > button'), {
        opacity: [.25, 1], x: [-14, 0], delay: stagger(85), duration: 520, ease: 'out(3)',
      }));
      self.add('press', (element) => animate(element, {
        scale: .96, duration: 120, ease: 'out(2)',
      }));
      self.add('release', (element) => animate(element, {
        scale: 1, ease: spring({ stiffness: 260, damping: 18 }),
      }));
      self.add('brand', (element) => animate(element, {
        scale: [{ to: 1.075, duration: 180 }, { to: 1, ease: spring({ stiffness: 220, damping: 16 }) }],
      }));
    });
    const listen = (element, event, callback) => {
      element.addEventListener(event, callback);
      listeners.push(() => element.removeEventListener(event, callback));
    };
    document.querySelectorAll('.zema-brand-logo').forEach((logo) => {
      const link = logo.closest('a');
      if (!link) return;
      listen(link, 'pointerenter', () => scope.methods.brand(logo));
      listen(link, 'focus', () => scope.methods.brand(logo));
    });
    document.querySelectorAll('.zema-button, .header-estimate, .auth-button').forEach((button) => {
      listen(button, 'pointerdown', () => scope.methods.press(button));
      ['pointerup', 'pointerleave', 'pointercancel', 'blur'].forEach((event) => {
        listen(button, event, () => scope.methods.release(button));
      });
    });
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) return;
        if (target.classList.contains('zema-home-market-bars')) scope.methods.chart(target);
        else if (target.classList.contains('zema-home-pipeline-copy')) scope.methods.pipeline(target);
        else scope.methods.reveal(target);
        observer.unobserve(target);
      });
    }, { threshold: .15 });
    document.querySelectorAll('.zema-process-card-copy, .zema-home-section-heading, .zema-home-pipeline-copy, .zema-home-value-copy, .zema-home-market-bars, .zema-home-result-copy, .zema-closing-preview, .services-hero__inner > div, .services-section-copy, .services-capability, .services-workspace__inner > *, .analytics-hero__inner > div, .analytics-market__copy, .analytics-snapshot, .analytics-method__inner > *, .about-section-head, .about-proof__rail > *, .about-ledger, .about-studio__copy, .about-principles__grid > *, .about-boundary > *, .contacts-section-head, .contacts-routes__grid > *, .contacts-desk__meta, .contacts-form-card, .contacts-response > *, .footer-home-statement, .footer-top > *').forEach((element) => observer.observe(element));
    return () => {
      observer.disconnect();
      listeners.forEach((remove) => remove());
      scope.revert();
    };
  }, [pathname, reducedMotion]);

  return null;
}
